/* JMT production cut rules.
 *
 * This file is the only storefront source of truth for translating customer
 * fit selections into private, merchant-facing cutting instructions. Keep the
 * customer adjustment properties unchanged; the underscore-prefixed JMT
 * properties are hidden from customers at checkout and remain available on
 * the Shopify order for production.
 */
(function (global) {
  'use strict';

  if (global.JMT_PRODUCTION) return;

  var VERSION = 'JMT-CUT-v4';
  var PROPERTY_KEYS = Object.freeze({
    adjustmentSummary: '_Pattern Adjustment'
  });
  var LEGACY_PROPERTY_KEYS = Object.freeze([
    '_JMT Cut Sheet',
    '_JMT Pouch Pattern',
    '_JMT Back Pattern',
    '_JMT Cut Rule'
  ]);
  var POUCH_PATTERNS = Object.freeze(['B2', 'B1', '0', 'A1', 'A2', 'A3']);
  var BACK_PATTERNS = Object.freeze([
    'Small -2',
    'Small -1',
    'Small',
    'Medium-XL',
    '2X-4X',
    '5X-7X',
    '5X-7X +1',
    '5X-7X +2'
  ]);

  function clean(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function normalizeAdjustment(value) {
    var normalized = clean(value).replace(/\s*(?:inches?|in\.?|\")\s*$/i, '');
    if (!normalized || normalized === '0') return 0;
    if (!/^[+-]?\d+$/.test(normalized)) return null;
    return parseInt(normalized, 10);
  }

  function sizeCode(value) {
    var normalized = clean(value).toUpperCase().replace(/\bSIZE\b/g, '').trim();
    var first = normalized.split(/[\s/(]/)[0].replace(/[^A-Z0-9]/g, '');

    if (first === 'S' || first === 'SMALL') return 'S';
    if (first === 'M' || first === 'MEDIUM') return 'M';
    if (first === 'L' || first === 'LARGE') return 'L';
    if (first === 'XL' || first === '1X' || first === '1XL' || first === 'EXTRALARGE') return 'XL';
    if (first === 'XXL') return '2X';

    var numeric = first.match(/^([2-7])X(?:L)?$/);
    return numeric ? numeric[1] + 'X' : '';
  }

  function displaySize(code) {
    if (code === 'S') return 'Small';
    if (code === 'M') return 'Medium';
    if (code === 'L') return 'Large';
    return code;
  }

  function pouchBaseIndex(code) {
    if (['S', 'M', 'L', 'XL', '2X'].indexOf(code) !== -1) return 2;
    if (['3X', '4X', '5X', '6X', '7X'].indexOf(code) !== -1) return 3;
    return -1;
  }

  function backBaseIndex(code) {
    if (code === 'S') return 2;
    if (['M', 'L', 'XL'].indexOf(code) !== -1) return 3;
    if (['2X', '3X', '4X'].indexOf(code) !== -1) return 4;
    if (['5X', '6X', '7X'].indexOf(code) !== -1) return 5;
    return -1;
  }

  function optionRole(name) {
    var normalized = clean(name).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (normalized.indexOf('waist') !== -1 || normalized === 'size') return 'size';
    if (normalized.indexOf('pouch') !== -1 || normalized.indexOf('cut') !== -1) return 'pouchStyle';
    if (normalized.indexOf('thong strap') !== -1 || normalized.indexOf('strap style') !== -1 || normalized.indexOf('back style') !== -1) return 'backStyle';
    return '';
  }

  function formatPouchStyle(value) {
    var style = clean(value);
    if (!style) return '';
    return /\bcut$/i.test(style) ? style : style + ' Cut';
  }

  function formatBackStyle(value) {
    var style = clean(value);
    if (!style) return '';
    return /\bback$/i.test(style) ? style : style + ' Back';
  }

  function failure(code, message, details) {
    return {
      ok: false,
      code: code,
      message: message,
      details: details || {},
      ruleVersion: VERSION
    };
  }

  function resolve(input) {
    input = input || {};
    var code = sizeCode(input.size);
    var pouchAdjustment = normalizeAdjustment(input.pouchAdjustment);
    var backAdjustment = normalizeAdjustment(input.backAdjustment);

    if (!code) {
      return failure('UNKNOWN_SIZE', 'The production pattern could not be calculated because the waist size was not recognized.', { size: clean(input.size) });
    }
    if (pouchAdjustment == null || backAdjustment == null) {
      return failure('INVALID_ADJUSTMENT', 'The production pattern could not be calculated because an adjustment value was not recognized.');
    }

    var pouchIndex = pouchBaseIndex(code) + pouchAdjustment;
    var backIndex = backBaseIndex(code) + backAdjustment;

    if (pouchIndex < 0 || pouchIndex >= POUCH_PATTERNS.length) {
      return failure('POUCH_PATTERN_OUT_OF_RANGE', 'That pouch adjustment is not available for ' + displaySize(code) + '.', {
        size: code,
        adjustment: pouchAdjustment
      });
    }
    if (backIndex < 0 || backIndex >= BACK_PATTERNS.length) {
      return failure('BACK_PATTERN_OUT_OF_RANGE', 'That back strap adjustment is not available for ' + displaySize(code) + '. Please choose one of the available adjustments.', {
        size: code,
        adjustment: backAdjustment
      });
    }

    var pouchPattern = POUCH_PATTERNS[pouchIndex];
    var backPattern = BACK_PATTERNS[backIndex];
    var parts = [
      formatPouchStyle(input.pouchStyle),
      displaySize(code),
      formatBackStyle(input.backStyle),
      'Pouch ' + pouchPattern,
      'Back Pattern ' + backPattern
    ].filter(Boolean);
    var hasAdjustments = pouchAdjustment !== 0 || backAdjustment !== 0;
    var adjustedPouchStyle = formatPouchStyle(input.pouchStyle);
    var adjustedBackStyle = formatBackStyle(input.backStyle);

    if (pouchAdjustment !== 0) adjustedPouchStyle += ' Pouch ' + pouchPattern;
    if (backAdjustment !== 0) adjustedBackStyle += ' Pattern ' + backPattern;

    var adjustmentParts = [
      adjustedPouchStyle,
      displaySize(code),
      adjustedBackStyle
    ].filter(Boolean);

    return {
      ok: true,
      ruleVersion: VERSION,
      size: code,
      pouchPattern: pouchPattern,
      backPattern: backPattern,
      cutSheet: parts.join(' | '),
      hasAdjustments: hasAdjustments,
      adjustmentSummary: hasAdjustments ? adjustmentParts.join(' | ') : '',
      adjustments: {
        pouch: pouchAdjustment,
        back: backAdjustment
      }
    };
  }

  function stripProductionProperties(properties) {
    var next = Object.assign({}, properties || {});
    Object.keys(PROPERTY_KEYS).forEach(function (name) {
      delete next[PROPERTY_KEYS[name]];
    });
    LEGACY_PROPERTY_KEYS.forEach(function (name) {
      delete next[name];
    });
    return next;
  }

  function withProductionProperties(properties, input) {
    var next = stripProductionProperties(properties);
    var result = resolve(input);
    if (!result.ok) return Object.assign({}, result, { properties: next });

    if (result.adjustments.pouch === 0) delete next['Pouch size adjustment'];
    if (result.adjustments.back === 0) delete next['Thong strap length adjustment'];
    if (result.hasAdjustments) next[PROPERTY_KEYS.adjustmentSummary] = result.adjustmentSummary;
    return Object.assign({}, result, { properties: next });
  }

  function selectionInput(optionNames, optionValues, adjustments) {
    var input = {
      pouchStyle: '',
      size: '',
      backStyle: '',
      pouchAdjustment: adjustments && adjustments.pouchAdjustment,
      backAdjustment: adjustments && (adjustments.backAdjustment != null ? adjustments.backAdjustment : adjustments.backStrapAdjustment)
    };

    (optionNames || []).forEach(function (name, index) {
      var role = optionRole(name);
      if (role) input[role] = optionValues && optionValues[index];
    });
    return input;
  }

  function inputFromProductVariant(product, variant, adjustments) {
    var optionNames = (product && product.options || []).map(function (option) {
      return typeof option === 'string' ? option : option && option.name;
    });
    var optionValues = variant && Array.isArray(variant.options)
      ? variant.options
      : optionNames.map(function (_name, index) { return variant && variant['option' + (index + 1)]; });
    return selectionInput(optionNames, optionValues, adjustments || {});
  }

  function inputFromFormData(formData) {
    var input = {
      pouchStyle: '',
      size: '',
      backStyle: '',
      pouchAdjustment: formData.get('properties[Pouch size adjustment]') || '',
      backAdjustment: formData.get('properties[Thong strap length adjustment]') || ''
    };

    if (!formData || typeof formData.forEach !== 'function') return input;
    formData.forEach(function (value, key) {
      var match = String(key).match(/^options\[(.+)\]$/);
      if (!match) return;
      var role = optionRole(match[1]);
      if (role) input[role] = value;
    });
    return input;
  }

  function enrichFormData(formData) {
    var input = inputFromFormData(formData);
    if (!input.size || (!input.pouchStyle && !input.backStyle)) return null;
    var result = resolve(input);
    if (!result.ok) return result;

    Object.keys(PROPERTY_KEYS).forEach(function (name) {
      formData.delete('properties[' + PROPERTY_KEYS[name] + ']');
    });
    LEGACY_PROPERTY_KEYS.forEach(function (name) {
      formData.delete('properties[' + name + ']');
    });
    if (result.adjustments.pouch === 0) formData.delete('properties[Pouch size adjustment]');
    if (result.adjustments.back === 0) formData.delete('properties[Thong strap length adjustment]');
    if (result.hasAdjustments) {
      formData.append('properties[' + PROPERTY_KEYS.adjustmentSummary + ']', result.adjustmentSummary);
    }
    return result;
  }

  function allowedBackAdjustments(size) {
    var base = backBaseIndex(sizeCode(size));
    if (base < 0) return [];
    return [-2, -1, 0, 1, 2].filter(function (adjustment) {
      var index = base + adjustment;
      return index >= 0 && index < BACK_PATTERNS.length;
    });
  }

  global.JMT_PRODUCTION = Object.freeze({
    VERSION: VERSION,
    PROPERTY_KEYS: PROPERTY_KEYS,
    POUCH_PATTERNS: POUCH_PATTERNS,
    BACK_PATTERNS: BACK_PATTERNS,
    optionRole: optionRole,
    sizeCode: sizeCode,
    resolve: resolve,
    selectionInput: selectionInput,
    inputFromProductVariant: inputFromProductVariant,
    inputFromFormData: inputFromFormData,
    stripProductionProperties: stripProductionProperties,
    withProductionProperties: withProductionProperties,
    enrichFormData: enrichFormData,
    allowedBackAdjustments: allowedBackAdjustments
  });
})(window);
